<template>
  <base-dialog :visible.sync="visible" width="560px" class="select-fields">
    <template v-slot:header>
      选择字段
    </template>
    <div class="search-wrapper">
      <el-input
        placeholder="搜索"
        prefix-icon="el-icon-search"
        clearable
        v-model="searchText"
      ></el-input>
    </div>
    <div class="scroll-wrapper" v-ps>
      <el-tree
        ref="tree"
        :props="{
          label: 'label',
          children: 'subList',
          isLeaf: 'isLeaf'
        }"
        :load="loadNode"
        node-key="id"
        :filter-node-method="filterNode"
        :default-expanded-keys="defaultExpandedKeys"
        lazy
        show-checkbox
        check-strictly
        :expand-on-click-node="false"
        @check-change="checkChange"
      >
        <template v-slot="{ node, data }">
          <div class="row">
            <span v-tip class="label">{{ data.label }}</span>
            <base-button
              class="select-all-button"
              v-if="data.refSheetId && from !== 'search'"
              type="text"
              @click="selectAll(node)"
            >
              全选
            </base-button>
          </div>
        </template>
      </el-tree>
    </div>
    <template v-slot:footer>
      <div class="dialog-footer">
        <base-button type="text" @click="selectAll">全选</base-button>
        <span class="count">已选: {{ selectedFields.length }} 个</span>
        <div class="right">
          <base-button type="primary" @click="save">保存</base-button>
          <base-button @click="visible = false">取消</base-button>
        </div>
      </div>
    </template>
  </base-dialog>
</template>

<script>
import BaseDialog from "@/components/UIComponents/BaseDialog"
import BaseButton from "@/components/UIComponents/Button"
import { getAllDefaultControls, getSheet } from "@/api/sheet/sheet"
import { disabledFieldTypes, transFieldControl } from "@/utils/sheet"
import { bfs } from "@/utils/algorithm"
import { systemControls } from "@/views/Backend/AdminEdit/Workflow/components/static"

export default {
  name: "select-fields",
  props: {
    hideSubLevel: Boolean,
    excludeReadonlyControl: Boolean,
    controlMapper: Function,
    level: Number,
    from: {
      type: String,
      default: "fill"
    }
  },
  components: {
    BaseDialog,
    BaseButton
  },
  data() {
    return {
      systemControls,
      visible: false,
      controlTypeMap: {},
      defaultExpandedKeys: [],
      selectedFields: [],
      sheetId: "",
      searchText: "",
      rootNode: {}
    }
  },
  watch: {
    searchText() {
      this.$refs.tree.filter(this.searchText)
    }
  },
  created() {
    getAllDefaultControls().then(res => (this.controlTypeMap = this._.keyBy(res, "_type")))
  },
  methods: {
    open(sheetId, selectedFields) {
      this.sheetId = sheetId
      this.defaultExpandedKeys = selectedFields
      this.selectedFields = selectedFields
      this.searchText = ""
      this.visible = true
      this.$nextTick(() => {
        // 重新触发load达到刷新效果,防止字段与工作表不一致
        let _node = this.rootNode
        _node.loaded = false
        _node.expand()
        this.$refs.tree.setCheckedKeys(selectedFields)
      })
    },
    save() {
      this.$emit("submit", this.$refs.tree.getCheckedKeys())
      this.visible = false
    },
    checkChange(control, checked) {
      let checkedKeys = this.$refs.tree.getCheckedKeys()
      this.selectedFields = checkedKeys
      if (checked) {
        // 子控件选中,则必选中父控件
        let parentId = control.id.slice(0, control.id.lastIndexOf("_"))
        if (!checkedKeys.includes(parentId)) {
          this.$refs.tree.setCheckedKeys(checkedKeys.concat(parentId))
        }
      } else {
        // 父控件清除,则清除所有子控件
        let newKeys = checkedKeys.filter(key => !key.startsWith(control.id))
        if (newKeys.length != checkedKeys.length) {
          this.$refs.tree.setCheckedKeys(newKeys)
        }
      }
    },
    loadNode(node, resolve) {
      const { level, data } = node
      if (data?.isLeaf) return resolve([])
      if (level === 0) this.rootNode = node
      let sheetId = level === 0 ? this.sheetId : data?.refSheetId
      if (!sheetId) return resolve([])
      const idPrefix = data ? data.id + "_" : ""
      getSheet(sheetId)
        .then(sheet => {
          resolve(
            (this.from === "search"
              ? (sheet.controls || []).concat(this.systemControls)
              : sheet.controls || []
            )
              .map(control => Object.assign({}, control))
              .map(control => (this.controlMapper ? this.controlMapper(control) : control))
              .filter(control => control._cid !== data?._ccid && control._type !== "chart")
              .filter(control => {
                if (control.hidden) return false // hidden:虚拟属性,可以通过外部传入的controlMapper设置,辅助隐藏
                // 需要放在 transFieldControl 之前,因为关联表字段不可编辑
                return !(this.excludeReadonlyControl && disabledFieldTypes.includes(control._type))
              })
              .map(control => transFieldControl(control))
              .map(control =>
                Object.assign(control, {
                  id: idPrefix + control._cid,
                  label: `[${control.isField ? "_" : ""}${this.controlTypeMap[control._type]
                    ?.name || "未知"}] ${control.name}`,
                  isLeaf:
                    control.isLeaf ||
                    this.hideSubLevel ||
                    (this.level === 0
                      ? !(control.refSheetId && this.level === level)
                      : !control.refSheetId),
                  disabled: false
                })
              )
          )
        })
        .catch(() => resolve([]))
    },
    filterNode(value, data) {
      if (!value) return true
      return data.label.includes(value)
    },
    selectAll(node) {
      node = node || this.rootNode
      let keys = []
      bfs({
        arr: [node],
        childFuc: node => node.childNodes,
        cbk: node => node.key && node.visible && keys.push(node.key)
      })
      this.selectedFields = this._.uniq(this.selectedFields.concat(keys))
      this.$refs.tree.setCheckedKeys(this.selectedFields)
    }
  }
}
</script>

<style lang="scss" scoped>
.select-fields {
  ::v-deep {
    .el-dialog__body {
      padding: 0 !important;
      display: flex;
      flex-flow: column;
      .el-tree-node__content {
        height: 32px;
      }
    }
  }
  .search-wrapper {
    box-sizing: border-box;
    padding: 18px 22px 12px;
  }
  .scroll-wrapper {
    flex: 1;
    .row {
      flex: 1;
      padding-right: 22px;
      display: flex;
      align-items: center;
      height: 32px;
      &:hover {
        .select-all-button {
          display: unset;
        }
      }
      .select-all-button {
        display: none;
      }
    }
  }
  .dialog-footer {
    flex: 1;
    display: flex;
    align-items: center;
    .count {
      margin-left: 10px;
    }
    .right {
      margin-left: auto;
    }
  }
}
</style>
